import TGApi from 'lib/webapp-api';
import fetcher from 'lib/fetcher';
import useBoundStore from 'store';
import { APIUserObj } from './types';
import { formatAuthCode, formatPhone } from './formatters';
import { NotificationFilter } from './hooks/use-notification-create-filter';
import { ApartmentFilterNotification } from './hooks/use-apartment-filter-notification';
import webAppApi from '..//lib/webapp-api'

type SignInAPIRes = { phone: string; code?: string };

type OKRes = { ok: true };
type SaleAddRes = { id: number };
type OKResBooking = { id: number };

export const signIn = async ({
  phone,
  personalDataConsent,
  privacyPolicyConsent,
  marketingConsent,
}: {
  phone: string;
  personalDataConsent: boolean;
  privacyPolicyConsent: boolean;
  marketingConsent: boolean;
}) => {
  const context = TGApi.context
  const initData = TGApi.getInitData()
  const res = await fetcher.post<SignInAPIRes>('/auth/sign-in', {
    phone: formatPhone(phone),
    personalDataConsent,
    privacyPolicyConsent,
    marketingConsent: marketingConsent === false ? undefined : marketingConsent,
    telegramInitData: context === 'telegram' ? initData : undefined,
    maxInitData: context === 'max' ? initData : undefined,
  });

  if (res.result) {
    useBoundStore.setState({ authPhone: phone });

    if (res.result.code) {
      const code = res.result.code;
      const authCode = code.slice(0, 3) + ' ' + code.slice(3);
      useBoundStore.setState({ authCode });
    }
  }

  return res;
};

type AuthorizeAPIRes = { user: APIUserObj; token: string } | { user: null; token: null };

export const authorize = async (context: typeof webAppApi['context'], initData: string) => {

  const { result } = await fetcher.post<AuthorizeAPIRes>('/auth', {
    telegramInitData: context === 'telegram' ? initData : undefined,
    maxInitData: context === 'max' ? initData : undefined,
  });

  if (result?.user) {
    useBoundStore.getState().setUser(result.user, result.token);
  }
};

export const resendAuthCode = async (phone: string) => {
  return fetcher.post('/auth/send-otp-code', {
    phone: formatPhone(phone),
  });
};

type VerifyAuthCodeAPIRes = { user: APIUserObj; token: string };

export const verifyAuthCode = async (phone: string, code: string) => {
  const context = TGApi.context
  const initData = TGApi.getInitData()
  const res = await fetcher.post<VerifyAuthCodeAPIRes>('/auth/verify-otp-code', {
    phone: formatPhone(phone),
    otpCode: formatAuthCode(code),
    telegramInitData: context === 'telegram' ? initData : undefined,
    maxInitData: context === 'max' ? initData : undefined,
  });

  if (res.result) {
    const { user, token } = res.result;
    useBoundStore.getState().setUser(user, token);
  }

  return res;
};

export const logOut = async () => {
  return fetcher.patch<OKRes>('auth/sign-out', { errorToast: true });
};

type UpdateUserInfoAPIRes = APIUserObj;

export const updateUserInfo = async (name: string) => {
  const res = await fetcher.patch<UpdateUserInfoAPIRes>('/users/update', { name });

  const user = res.result;
  if (user) {
    useBoundStore.getState().setUser(user);
  }

  return res;
};

export const removeRealtorByRealtor = async (id: number) => {
  return await fetcher.patch<OKRes>(`/users/realtor/${id}/unbind`);
};

export const removeRealtorByClient = async (id: number) => {
  return await fetcher.delete<OKRes>(`/users/realtor/${id}`);
};

export const removeClient = async (id: number) => {
  return await fetcher.patch<OKRes>(`/users/client/${id}/unbind`);
};

/* ------------------------------------------------------------------------------------ */

export const complainAboutApartment = async (id: number, reasons: string[]) => {
  return fetcher.post<{}>('/facilities/complaint', { id, reasons }, { errorToast: true });
};

export const generatePrimaryApartmentReport = async (
  apartmentData: { id: number },
  reportSourceInfo?: { notificationId?: number; favouritesListId?: number },
) => {
  return fetcher.post<OKRes>(`/facilities/${apartmentData?.id}/report`, {
    notificationId: reportSourceInfo?.notificationId,
    favouritesListId: reportSourceInfo?.favouritesListId,
    platform: webAppApi.context
  });
};

export const generateSecondaryApartmentReport = async (
  apartmentData: { id: number },
  reportSourceInfo?: { notificationId?: number; favouritesListId?: number },
) => {
  return fetcher.post<OKRes>(`/facilities/${apartmentData?.id}/secondary-report`, {
    notificationId: reportSourceInfo?.notificationId,
    favouritesListId: reportSourceInfo?.favouritesListId,
    platform: webAppApi.context
  });
};

export const generateDealBooking = async (dealRequestId: number, facilityId: number) => {
  return fetcher.post<OKRes>(`/deal-requests/booking`, {
    facilityId: facilityId,
    dealRequestId: dealRequestId,
  });
};

export const requestApartmentBooking = async (id: number) => {
  return fetcher.post<OKResBooking>(
    '/deal-requests',
    { dealRequestType: 'book', facilityId: id },
    { errorToast: true },
  );
};

export const requestApartmentPurchase = async (id: number) => {
  return fetcher.post<OKRes>(
    '/deal-requests',
    { dealRequestType: 'buy', facilityId: id },
    { errorToast: true },
  );
};

export const requestApartmentConsultation = async (id: number) => {
  return fetcher.post<OKRes>(
    '/deal-requests',
    { dealRequestType: 'consult', facilityId: id },
    { errorToast: true },
  );
};

/* ------------------------------------- Избранные ------------------------------------ */

export const favApartment = async (id: number) => {
  return fetcher.patch<{ success: true }>(`/favourites/facilities/${id}`, null, {
    errorToast: true,
  });
};

export const unfavApartment = async (id: number) => {
  return fetcher.delete<{ success: true }>(`/favourites/facilities/${id}`, null, {
    errorToast: true,
  });
};

export const toggleFavApartment = async <T extends { id: number; isFavorite?: boolean }>(
  apartment: T,
  mutateFunc: () => void,
) => {
  const func = apartment.isFavorite ? unfavApartment : favApartment;
  const res = await func(apartment.id);
  mutateFunc();

  if (apartment.isFavorite) {
    useBoundStore.getState().showToast({
      message: 'Объект удалён из избранного',
      dissmissLabel: 'Отменить',
      onDismissClick: () => {
        favApartment(apartment.id).finally(mutateFunc);
      },
    });
  }

  return res;
};

/* ------------------------------------------------------------------------------------ */

export type APISaleAd = {
  buildingId: number;
  apartmentNumber: string;
  areaInSqM?: string;
  price: number;
  expiresInDays: number | undefined;
  marketType?: string;
  // roomCount?: string;
  typeId?: number;
};

export type CreateSaleAd = {
  result: {
    id: number;
  };
};

export const createSaleAd = async (payload: APISaleAd) => {
  return fetcher.post<SaleAddRes>('/facilities', payload, { errorToast: true });
};

export const editSaleAd = async (
  id: number | undefined,
  price: number | null,
  loweredPrice?: number,
  hasMaternityCapital?: boolean,
  hasBurden?: boolean,
  minorOwners?: boolean,
) => {
  return fetcher.patch<OKRes>(
    `/facilities/${id}`,
    { loweredPrice, price, hasMaternityCapital, hasBurden, minorOwners },
    { errorToast: true },
  );
};

export const deleteSaleAd = async (id: number) => {
  return fetcher.delete<OKRes>(`/facilities/${id}`, {}, { errorToast: true });
};

export const uploadDocuments = async (id: number, formData: any) => {
  return fetcher.post<OKRes>(`/facilities/${id}/document`, formData);
};

export const uploadFacilitiesPhoto = async (id: number, formData: any) => {
  return fetcher.post<{
    id: number;
  }>(`/facilities/${id}/media`, formData);
};

/* ------------------------------------------------------------------------------------ */

export const createCallRequest = async () => {
  return fetcher.post<OKRes>('/call-request', {}, { errorToast: true });
};

/* ----------------------------------Уведомления--------------------------------------- */

export type FacilityNotification = {
  success: boolean;
};

export const toggleNotification = async (facilityId: number, mutate: () => void) => {
  const res = await fetcher.patch<FacilityNotification>(
    `/facility-notifications/${facilityId}/toggle`,
    {},
    { errorToast: true },
  );
  mutate();
  return res;
};

export const toggleStatusNotification = async (id: number) => {
  return fetcher.patch<{ success: true }>(`/facility-notifications/${id}/toggle`, null, {
    errorToast: true,
  });
};

export const deleteNotification = async (id: number) => {
  return fetcher.delete<{ success: true }>(`/facility-notifications/${id}`, null, {
    errorToast: true,
  });
};

export const changeNotificationStatus = async <
  T extends {
    id: number;
    notificationEnabled?: boolean;
    notification: { id: number; isEnabled: boolean };
  },
>(
  notification: T,
  mutateFunc: () => void,
) => {
  const func = !!notification?.notificationEnabled ? deleteNotification : toggleStatusNotification;
  const res = await func(
    !!notification?.notificationEnabled ? notification?.notification?.id : notification?.id,
  );
  mutateFunc();

  if (notification.notificationEnabled) {
    useBoundStore.getState().showToast({
      message: 'Объект удалён из уведомлений',
      dissmissLabel: 'Отменить',
      onDismissClick: () => {
        toggleStatusNotification(notification?.id).finally(mutateFunc);
      },
    });
  }

  return res;
};

export const deleteFilter = async (id: number) => {
  return fetcher
    .delete<{ success: true }>(`/filter-notifications/${id}`, null, {
      errorToast: true,
    })
    .then(() =>
      useBoundStore.getState().showToast({
        message: 'Фильтр уведомлений удалён',
      }),
    );
};

export const deleteFilterNotificationStatus = async <T extends ApartmentFilterNotification>(
  notificationId: number,
  mutateFunc: () => void,
) => {
  const res = await deleteFilter(notificationId);
  mutateFunc();
  return res;
};

export const createFilterNotification = async (createFilterData: NotificationFilter) => {
  const { result } = await fetcher.post<{ success: true }>(
    '/filter-notifications',
    createFilterData,
    {
      errorToast: true,
    },
  );

  if (!!result?.success) {
    useBoundStore.getState().showToast({
      message: 'Фильтр создан',
    });
  } else {
    useBoundStore.getState().showToast({
      message: 'Ошибка создания фильтра',
    });
  }
};

/* ----------------------------------Temporary--------------------------------------- */

export const toggleAddTemporary = async (facilityId: number) => {
  return fetcher.patch<FacilityNotification>(
    `/temporary-files/add-facilities?facilityIds=${facilityId}`,
    {},
    { errorToast: true },
  );
};

export const toggleDeleteTemporary = async (facilityId: number) => {
  return fetcher.delete<FacilityNotification>(
    `/temporary-files/delete-facilities?facilityIds=${facilityId}`,
    {},
    { errorToast: true },
  );
};

export const toggleTemporary = async (apartment: any, mutateFunc: () => void) => {
  const func = apartment.isSavedInTemporaryFiles ? toggleDeleteTemporary : toggleAddTemporary;
  const res = await func(apartment.id);
  mutateFunc();

  return res;
};

export const requestHideTemporary = async (facilityId: number) => {
  return fetcher.patch<FacilityNotification>(
    `facilities/${facilityId}/hide`,
    {},
    { errorToast: true },
  );
};

export const requestSyncCalendarLinkToChat = async () => {
  return fetcher.get<{
    webcal: string;
    https: string;
  }>(
    `events/feed/link`,
    {},
    { errorToast: true }
  )
}
